"use client";

import React, { useState } from "react";
import { CircleCheck as CheckCircle2, Truck, ExternalLink, MessageCircle, CircleAlert as AlertCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Dhl() {
  const [trackingId, setTrackingId] = useState("");
  const [error, setError] = useState("");
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const handleTrack = () => {
    if (!trackingId.trim()) {
      setError("Por favor, escribe tu número de guía.");
      return;
    }
    setError("");
    const url = `https://www.dhl.com/global-en/home/tracking/tracking-express.html?submit=1&tracking-id=${trackingId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const securityPromises = [{
    title: "1) Salida desde México",
    desc: "Tu envío se prepara desde origen con cuidado y control."
  }, {
    title: "2) Guía y seguimiento",
    desc: "Recibes tu número de guía para rastrear el trayecto."
  }, {
    title: "3) Entrega internacional",
    desc: "Tus documentos viajan a España, EUA y otros destinos del mundo."
  }];
  const jsonLd = {
    "@context": "https://schema.org/",
    "@type": "Service",
    "name": "Envío de Documentos Apostillados DHL - Papeles Sin Miedo",
    "description": "Servicio especializado en el envío de documentos apostillados y apostillas internacionales desde México a España, Estados Unidos y el mundo con rastreo DHL en tiempo real.",
    "keywords": "apostilla, antecedentes no penales apostillados, actas apostilladas, apostillas internacionales, envío de documentos méxico",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Papeles Sin Miedo"
    },
    "areaServed": ["MX", "ES", "US"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Servicios de Mensajería y Apostillas",
      "itemListElement": [{
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Envío de Documentos Apostillados e Internacionales"
        }
      }]
    }
  };
  return <>
      <section id="dhl" className="relative bg-background py-16 lg:py-24 px-4 overflow-hidden bg-cover bg-center bg-no-repeat bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1596556/img/mexico_to_world_full_background.png)]" role="img" aria-label="Logística internacional y rastreo de envíos DHL: Envío seguro de documentos desde México a España, Estados Unidos y destinos globales">
        <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(jsonLd)
      }} />
        
        <div className="absolute inset-0 bg-background/85 pointer-events-none" />

        <div ref={ref} className={`relative z-10 max-w-6xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="flex flex-col items-center">
            
            <div className="w-full max-w-3xl">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6 leading-tight text-center lg:text-left">
                Tus documentos apostillados salen de México con DHL y llegan al mundo
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed text-center lg:text-left">
                Cuando tus documentos están listados, los enviamos desde México por DHL a España, Estados Unidos y otros países, con seguimiento claro.
              </p>

              <p className="text-lg text-muted-foreground mb-10 leading-relaxed text-center lg:text-left">
                Así sabes dónde va tu envío y puedes recibirlo con más tranquilidad.
              </p>

              <div className="bg-card border border-border rounded-xl p-6 lg:p-8 shadow-sm mb-10">
                <h3 className="text-xl lg:text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                  <Truck className="size-6 text-primary" />
                  Lo que cuidamos por ti
                </h3>
                
                <ul className="space-y-6">
                  {securityPromises.map((item, index) => <li key={index} data-index={index} className="flex gap-4">
                      <CheckCircle2 className="size-6 text-[#28A745] shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-foreground text-base lg:text-lg">{item.title}</h4>
                        <p className="text-muted-foreground text-sm lg:text-base">{item.desc}</p>
                      </div>
                    </li>)}
                </ul>
              </div>

              <div className="space-y-4">
                <div className="bg-accent/30 rounded-xl p-6 border border-border/50">
                  <div className="flex flex-col gap-4">
                    <div className="space-y-2">
                      <label htmlFor="tracking-input" className="text-sm font-medium text-foreground px-1">
                        Si tu guía de DHL ya fue generada, rastrea aquí tu envío.
                      </label>
                      <input id="tracking-input" type="text" placeholder="Ej: 1234567890" value={trackingId} onChange={e => setTrackingId(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:ring-2 focus:ring-[#28A745]/20 focus:border-[#28A745] outline-none transition-all text-base" />
                      {error && <p className="text-destructive text-sm flex items-center gap-1.5 mt-1 px-1">
                          <AlertCircle className="size-4" />
                          {error}
                        </p>}
                    </div>

                    <Button onClick={handleTrack} className="w-full h-12 text-lg font-bold bg-[#D40511] hover:bg-[#B3040E] text-white rounded-lg shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]">
                      Rastrear envío
                      <ExternalLink className="size-5 ml-2" />
                    </Button>
                  </div>
                </div>

                <div className="pt-4 text-center lg:text-left">
                  <p className="text-sm text-muted-foreground mb-2">
                    ¿No encuentras tu guía? Te ayudamos por WhatsApp.
                  </p>
                  <Link to="https://wa.me/message/4CMV5AWVJ56WL1" newTab className="inline-flex items-center gap-2 text-[#006F3D] font-bold hover:underline transition-all">
                    <MessageCircle className="size-5 fill-[#006F3D]/10" />
                    Contactar por WhatsApp
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>;
}